import { useEffect, useState } from "react";
import { api, idr, openDocument, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ReceiptText, Mail, Eye, FileDown, ExternalLink, Send, CircleDollarSign, CheckCircle2, MessageSquare, Download, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DirectInvoiceDialog } from "@/components/DirectInvoiceDialog";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [sel, setSel] = useState(null);
  const [zohoUrl, setZohoUrl] = useState("https://billing.zoho.com/app");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const filteredInv = invoices.filter((inv) =>
    `${inv.invoice_number} ${inv.customer_name} ${inv.customer_company || ""}`.toLowerCase().includes(search.toLowerCase()) &&
    (!dateFrom || (inv.created_at || "").slice(0, 10) >= dateFrom) &&
    (!dateTo || (inv.created_at || "").slice(0, 10) <= dateTo)
  );
  const reload = async () => {
    const r = await api.get("/invoices");
    setInvoices(r.data);
    setSel((s) => (s ? r.data.find((i) => i.id === s.id) || s : s));
  };
  useEffect(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/config").then((r) => setZohoUrl(r.data.zoho_billing_url)).catch(() => {});
  }, []);

  const [syncing, setSyncing] = useState(false);
  const syncZoho = async () => {
    setSyncing(true);
    try {
      await api.post("/zoho/sync");
      toast.info("Sinkron dari Zoho berjalan (±1-2 menit)…");
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get("/zoho/sync/status");
        if (!data.running) {
          if (data.error) toast.error(`Sinkron gagal: ${data.error}`);
          else toast.success(`Sinkron selesai: ${data.counts?.invoices || 0} invoice, ${data.counts?.products || 0} produk, ${data.counts?.customers || 0} customer`);
          break;
        }
      }
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const sendZoho = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${sel.id}/zoho/send`);
      toast.success(data.message || "Invoice dikirim via Zoho");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const markPaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${sel.id}/mark-paid`);
      toast.success(data.zoho_paid ? "Lunas & tercatat di Zoho" : "Ditandai lunas (Zoho: " + (data.zoho_paid_message || "-") + ")");
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendWA = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${sel.id}/whatsapp`);
      toast.success("Invoice dikirim via WhatsApp");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const pushZoho = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${sel.id}/zoho/push`);
      toast.success(`Tersinkron ke Zoho: ${data.zoho_invoice_number || "OK"}`);
      await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader overline="Dokumen" title="Invoice">
        <button data-testid="zoho-sync-btn" onClick={syncZoho} disabled={syncing} className="flex items-center gap-2 border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary transition-colors disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sinkron…" : "Sinkron dari Zoho"}
        </button>
        <DirectInvoiceDialog onCreated={reload} />
        <button data-testid="export-invoices-btn" onClick={() => openDocument("/export/invoices.xlsx").catch(() => toast.error("Gagal ekspor"))} className="flex items-center gap-2 border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary transition-colors">
          <Download className="w-4 h-4" /> Ekspor Excel
        </button>
      </PageHeader>
      <div className="p-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="invoice-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no invoice / customer…" className="pl-9 rounded-sm bg-secondary" />
          </div>
          <Input data-testid="invoice-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 rounded-sm bg-secondary" />
          <span className="text-muted-foreground text-sm">s/d</span>
          <Input data-testid="invoice-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 rounded-sm bg-secondary" />
        </div>
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left overline text-muted-foreground">
                <th className="px-4 py-3">No. Invoice</th><th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th><th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredInv.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  <ReceiptText className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada invoice.</td></tr>
              )}
              {filteredInv.map((inv) => (
                <tr key={inv.id} className="hover:bg-secondary/40" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="px-4 py-3"><div className="font-medium">{inv.customer_name}</div><div className="text-xs text-muted-foreground">{inv.customer_company}</div></td>
                  <td className="px-4 py-3 font-mono">{idr(inv.total)}</td>
                  <td className="px-4 py-3">
                    {inv.email_sent ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><Mail className="w-3 h-3" /> Terkirim</span> : "-"}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDocument(`/invoices/${inv.id}/pdf`).catch(() => toast.error("Gagal membuka PDF"))} className="text-primary hover:underline text-xs inline-flex items-center gap-1 mr-3" data-testid={`pdf-invoice-${inv.id}`}>
                      <FileDown className="w-3 h-3" /> PDF
                    </button>
                    <button onClick={() => setSel(inv)} className="text-muted-foreground hover:text-foreground text-xs inline-flex items-center gap-1" data-testid={`view-invoice-${inv.id}`}>
                      <Eye className="w-3 h-3" /> Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{sel.invoice_number}</DialogTitle>
                <DialogDescription>Detail invoice & aksi pengiriman / sinkronisasi Zoho.</DialogDescription>
              </DialogHeader>
              <div className="text-sm space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{sel.customer_name}</span></div>
                {sel.customer_address?.street && (
                  <div className="flex justify-between gap-4" data-testid="invoice-customer-address">
                    <span className="text-muted-foreground">Alamat</span>
                    <span className="text-right">{[sel.customer_address.street, sel.customer_address.city, sel.customer_address.state, sel.customer_address.zip, sel.customer_address.country].filter(Boolean).join(", ")}</span>
                  </div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Ref. Penawaran</span><span className="font-mono text-xs">{sel.quote_number}</span></div>
                <div className="border border-border rounded-sm divide-y divide-border">
                  {sel.items.map((it, i) => (
                    <div key={i} className="px-3 py-2 flex justify-between">
                      <span>{it.product_name} <span className="text-muted-foreground">x{it.quantity}</span></span>
                      <span className="font-mono text-xs">{idr(it.line_total)}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{idr(sel.subtotal)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>PPN</span><span className="font-mono">{idr(sel.tax_amount)}</span></div>
                  <div className="flex justify-between font-heading font-black text-lg pt-2 border-t border-border"><span>Total</span><span className="font-mono">{idr(sel.total)}</span></div>
                </div>
                <div className="p-3 bg-secondary rounded-sm text-xs font-mono text-muted-foreground">{sel.email_log}</div>
                {sel.zoho_synced && (
                  <div className="p-3 border border-emerald-500/30 bg-emerald-500/5 rounded-sm text-xs" data-testid="zoho-sync-status">
                    <span className="text-emerald-700 font-medium">✓ Tersinkron ke Zoho Billing</span>
                    <div className="font-mono text-muted-foreground mt-1">No. Zoho: {sel.zoho_invoice_number}</div>
                  </div>
                )}
                {sel.zoho_synced === false && sel.zoho_error && (
                  <div className="p-3 border border-destructive/30 bg-destructive/5 rounded-sm text-xs text-destructive font-mono" data-testid="zoho-sync-error">
                    Zoho Billing: {sel.zoho_error}
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2 sm:gap-2 sm:space-x-0">
                <Button variant="outline" className="rounded-sm" onClick={() => openDocument(`/invoices/${sel.id}/pdf`).catch(() => toast.error("Gagal membuka PDF"))} data-testid="dialog-pdf-btn">
                  <FileDown className="w-4 h-4 mr-2" /> PDF
                </Button>
                {!sel.zoho_synced && (
                  <Button variant="outline" className="rounded-sm border-primary text-primary hover:bg-primary/5" onClick={pushZoho} disabled={busy} data-testid="zoho-push-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Sinkron ke Zoho
                  </Button>
                )}
                {sel.zoho_invoice_id && (
                  <Button variant="outline" className="rounded-sm" onClick={sendZoho} disabled={busy} data-testid="zoho-send-btn">
                    <Send className="w-4 h-4 mr-2" /> Kirim via Zoho
                  </Button>
                )}
                {sel.customer_phone && (
                  <Button variant="outline" className="rounded-sm border-emerald-600 text-emerald-700 hover:bg-emerald-50" onClick={sendWA} disabled={busy} data-testid="invoice-wa-btn">
                    <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                )}
                {sel.status !== "paid" ? (
                  <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={markPaid} disabled={busy} data-testid="mark-paid-btn">
                    <CircleDollarSign className="w-4 h-4 mr-2" /> Tandai Lunas
                  </Button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-700 text-sm px-2" data-testid="paid-indicator"><CheckCircle2 className="w-4 h-4" /> Lunas</span>
                )}
                <a href={sel.zoho_invoice_url || zohoUrl} target="_blank" rel="noreferrer" data-testid="zoho-billing-link">
                  <Button className="rounded-sm w-full"><ExternalLink className="w-4 h-4 mr-2" /> Zoho Billing</Button>
                </a>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
